/**
 * Editor chrome geometry — one definition, read by everything that needs it.
 *
 * Before this module the same numbers lived in four places that had to agree by
 * hand: `PANEL_W = 340` in panels/LeftPanel.tsx, `PANEL_W = 320` in
 * inspector/RightPanel.tsx, the `left-12` / `right-12` Tailwind classes on both
 * panels and both rails, and toolbar/SceneToolbar.tsx's
 * `ui.rightTab ? 320 + 48 + 12 : 48 + 12`. Adding the canvas card would have
 * made it five.
 */

/**
 * Width of either rail. 72px since the 2026-09-29 redesign: every rail item
 * carries its WORD under the icon (the 48px strips were icon-only, and 17
 * unlabelled icons were the single biggest reason the studio read as hard).
 * Measured off the approved mockup, as are the panel widths.
 */
export const RAIL_W = 72;
export const LEFT_PANEL_W = 300;
export const RIGHT_PANEL_W = 320;

/** Height of the studio top bar. */
export const TOPBAR_H = 56;

/** Ground margin between the chrome and the white canvas card. */
export const CARD_GAP = 12;

/**
 * Below this the panels collapse back to bare rails.
 *
 * ⚠️ This used to protect the canvas: panels reserved real layout width, and a
 * canvas squeezed toward zero is a GL error or a black frame, not a small
 * render. **Panels now overlay the canvas instead of squeezing it**, so the
 * drawing buffer is never at risk and that reason is gone. The threshold stays
 * for the plainer one: on a narrow window an open panel would cover nearly the
 * whole model, which is not a useful screen.
 *
 * `chromeInsets` is therefore no longer a canvas-sizing input. It positions
 * chrome that must sit clear of a panel — the floating scene toolbar — and
 * nothing else.
 */
export const MIN_CANVAS_W = 360;

export interface ChromeInsets {
  left: number;
  right: number;
}

/**
 * How much horizontal room the chrome occupies on each side.
 *
 * The rails are always visible, so they always count. The panels count only
 * when open — and `containerW` opts into the narrow-window fallback: pass it
 * and the panels give their width back once the canvas would drop below
 * `MIN_CANVAS_W`. Omit it (or pass 0, i.e. "not measured yet") and you get the
 * unclamped answer, which is the right default for a first paint.
 */
export function chromeInsets(
  leftPanel: string | null,
  rightTab: string | null,
  containerW = 0,
  mode: "basic" | "advanced" = "advanced",
): ChromeInsets {
  // Basic mode has no right rail: its one panel (Fine-tune) sits flush right.
  const rightRail = mode === "basic" ? 0 : RAIL_W;
  const left = RAIL_W + (leftPanel ? LEFT_PANEL_W : 0);
  const right = rightRail + (rightTab ? RIGHT_PANEL_W : 0);
  if (containerW > 0 && containerW - left - right < MIN_CANVAS_W) {
    return { left: RAIL_W, right: rightRail };
  }
  return { left, right };
}

/**
 * Box of the white canvas card, in the shell row's coordinates. Overlays that
 * belong over the MODEL (the dropzone, the load overlay, toasts) take this;
 * overlays that belong over the whole editor do not.
 */
export interface CardInset {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** The card box before the shell has measured anything — full bleed. */
export const ZERO_INSET: CardInset = { left: 0, right: 0, top: 0, bottom: 0 };
